import { useRef } from 'react';

import { ItemContent } from '@/components/item/itemContent';
import { ItemType } from '@/components/item/itemType';
import { useCollapsibleState } from '@/components/table/collapsibleCell';
import { CollapsibleRow } from '@/components/table/collapsibleRow';
import {
  Table,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useVirtualRows } from '@/hooks/useVirtualRows';
import type { Item } from '@/models/item/item';

/**
 * Catalog items, code over name. The list can run to hundreds, so the rows
 * are virtualized; a bundle opens on its content.
 */
export const ItemsTable = ({ items }: { items: Item[] }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { virtualRows, before, after, measureElement } = useVirtualRows({
    rowsCount: items.length,
    scrollRef,
    estimateSize: 53,
  });
  // Held here, not in each row: a row scrolled out of the window would forget it.
  const collapsible = useCollapsibleState<string>();

  return (
    // The scroll container itself: the virtualizer sizes its window from it.
    <div ref={scrollRef} className="relative max-h-80 overflow-y-auto">
      {/* table-fixed: the header sets the widths, so an opened bundle's
          spanning row cannot widen the chevron column. */}
      <Table
        noWrapper
        stickyHeader
        className="table-fixed [&_thead_th]:bg-background"
      >
        <TableHeader className="bg-transparent">
          <TableRow>
            <TableHead className="w-8">
              <span className="sr-only">Expand</span>
            </TableHead>
            <TableHead>Item</TableHead>
          </TableRow>
        </TableHeader>
        <tbody aria-hidden>
          <tr style={{ height: before }} />
        </tbody>
        {virtualRows.map((virtualRow) => {
          const item = items[virtualRow.index];
          if (!item) return null;
          return (
            <CollapsibleRow
              key={item.item_id}
              data-index={virtualRow.index}
              ref={measureElement}
              open={collapsible.isExpanded(item.item_id)}
              onToggle={() => {
                collapsible.toggle(item.item_id);
              }}
              expanded={
                item.type === 'bundle' && <ItemContent itemId={item.item_id} />
              }
              colSpan={2}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <ItemType
                    type={item.type}
                    item_rarity={item.item_rarity}
                    size={16}
                  />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-mono">{item.code}</span>
                    <span
                      className="truncate text-xs text-muted-foreground"
                      title={item.name}
                    >
                      {item.name}
                    </span>
                  </div>
                </div>
              </TableCell>
            </CollapsibleRow>
          );
        })}
        <tbody aria-hidden>
          <tr style={{ height: after }} />
        </tbody>
      </Table>
    </div>
  );
};
